// Standard library imports
import React, { useEffect, useState } from 'react';

// External library imports
import { UilTrash, UilCopy, UilEye, UilPen } from '@iconscout/react-unicons';
import { toast } from 'react-toastify';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Form, Formik } from 'formik';

// Internal module imports
import classes from '../../../styles/AllDevices.module.css';
import Typography from '../../../components/Typography/Typography';
import Table from '../../../components/Table/Table';
import { ApikeyService } from '../../../services/apiKeyService';
import { useLoader } from '../../../hooks';
import DeleteModal from '../../../components/DeleteModal/DeleteModal';
import { Input, ToggleButton, ToggleButtonWithState } from '../../../components/Inputs/Input';
import ModalComponent from '../../../components/ModalComponent/ModalComponent';
import { CreateApiValidation } from '../../../validations/Home/externalApiValidation';
import CustomTooltip from '../../../components/CustomToolTip/CustomTooltip';
import DropdownComponent from '../../../components/Inputs/Dropdown';
import { API_KEY_ACCESS } from '../../../constants';
import { Capitalize } from '../../../utils/stringHelper';

function ExternalApis({ customersData, customerId }) {
    const [startLoader, stopLoader] = useLoader();
    const [customers, setCustomers] = useState([]);
    const [customersApi, setCustomersApi] = useState([]);
    const [edgeDevices, setEdgeDevices] = useState([]);
    const [edgeGateways, setEdgeGateways] = useState([]);
    const [addkey, setAddKey] = useState({
        status: false,
        isEdit: false,
    });
    const [deleteModal, setDeleteModal] = useState({
        status: false,
        keyId: '',
    });
    const [apiKeyModal, setApiKeyModal] = useState({
        status: false,
        key: {},
    });

    const [initialValues, setInitialValues] = useState({
        name: '',
        customerId: customerId,
        isActive: false,
        access: API_KEY_ACCESS,
        edgeId: '',
    });

    useEffect(() => {
        if (customerId) {
            getdata();
        }
    }, []);

    useEffect(() => {
        if (addkey.status) {
            getIOTDevicesForCustomer();
            getIOTGatewaysForCustomer();
        }
    }, [addkey.status]);

    const getIOTDevicesForCustomer = () => {
        ApikeyService.GetIOTDevicesForCustomer(customerId, startLoader, handleIOTDevicesSucess, handleError, stopLoader);
    };

    const getIOTGatewaysForCustomer = () => {
        ApikeyService.getIOTGatewaysForCustomer(customerId, startLoader, handleIOTGatewaysSucess, handleError, stopLoader);
    };

    const getdata = () => {
        ApikeyService.GetAllApiKeyForCustomer(customerId, startLoader, handleFetchSucess, handleError, stopLoader);
    };

    const handleIOTDevicesSucess = ({ data }) => {
        const processedData = data.data.map((item) => {
            return {
                label: item.edgeAlias,
                value: item.deviceId,
            };
        });
        setEdgeDevices(processedData);
    };

    const handleIOTGatewaysSucess = ({ data }) => {
        const processedData = data.data.map((item) => {
            return {
                label: item.edgeGatewayAlias,
                value: item.gatewayId,
            };
        });
        setEdgeGateways(processedData);
    };

    const handleToggleChange = (item, id, value) => {
        let payLoad = {
            data: {
                isActive: value,
            },
        };
        ApikeyService.Update(item ? id : '', payLoad, startLoader, handleUpdateSuccess, handleError, stopLoader);
    };

    const handleUpdateSuccess = (res) => {
        if (res) {
            toast.success('Status updated!');
            getdata();
        }
    };

    const handleDelete = () => {
        ApikeyService.Delete(deleteModal?.keyId, startLoader, handleVersionDeleteSuccess, handleError, stopLoader);
    };

    const handleVersionDeleteSuccess = (res) => {
        if (res) {
            toast.success('Keys deleted successfully!');
            getdata();
        }
    };

    const handleError = (err) => {
        let data = err && err.response ? err.response.data : null;
        if (data) toast.error(data.message);
        else toast.error('Internal server error!');
    };

    const handleFetchSucess = ({ data }) => {
        console.log({ data });
        let newData = data?.data.map((item, index) => ({
            ...item,
            's.no': index + 1,
            name: item?.name,
            customerName: item?.customerName || '--',
            edge: item?.edgeAlias || '--',
            edgeGateway: item?.edgeGatewayAlias || '--',
            action: (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <div className={classes.ToggleContainer}>
                        <ToggleButtonWithState disabled={false} value={item.isActive} onChange={(e) => handleToggleChange(item, item._id, e.target.checked)} />
                    </div>
                    <div>
                        <CustomTooltip content={'View'}>
                            <UilEye
                                size={'1.2vw'}
                                style={{ color: 'var(--color-primary)', marginRight: '0.5vw' }}
                                onClick={() => {
                                    setApiKeyModal({ status: true, key: item });
                                }}
                            />
                        </CustomTooltip>
                    </div>

                    <div>
                        <CustomTooltip content={'Edit'}>
                            <UilPen
                                size={'1.2vw'}
                                style={{ color: 'var(--color-primary)' }}
                                onClick={() => {
                                    setAddKey({
                                        status: true,
                                        isEdit: true,
                                        _id: item._id,
                                    });
                                    setInitialValues({
                                        name: item?.name,
                                        customerId: item.customerId,
                                        access: item.access,
                                        isActive: item?.isActive,
                                        edgeId: item?.edgeId,
                                    });
                                }}
                            />
                        </CustomTooltip>
                    </div>

                    <div style={{ marginLeft: '0.3vw' }}>
                        <CustomTooltip content={'Delete'}>
                            <UilTrash size={'1.2vw'} style={{ color: 'var(--color-primary)' }} onClick={() => setDeleteModal({ status: true, keyId: item._id })} />
                        </CustomTooltip>
                    </div>
                </div>
            ),
        }));
        setCustomers(newData);
    };

    useEffect(() => {
        let temp = [];
        customersData.forEach((element) => {
            temp.push({ value: element._id, label: element.name });
        });
        setCustomersApi(temp);
    }, [JSON.stringify(customersData)]);

    const handleApiCreate = (values) => {
        let payload = {
            data: {
                customerId: values.customerId,
                isActive: values.isActive,
                name: values?.name,
                access: values.access,
                ...(values.edgeId ? { edgeId: values.edgeId } : {}),
            },
        };
        if (addkey.isEdit) {
            ApikeyService.Update(addkey?._id, payload, startLoader, handleAccessUpdateSuccess, handleError, stopLoader);
        } else {
            ApikeyService.Create(payload, startLoader, handleCreateSuccess, handleError, stopLoader);
        }
    };

    const handleCreateSuccess = (res) => {
        if (res) {
            toast.success('API Key Created');
            setAddKey({
                status: false,
                isEdit: false,
            });
            setInitialValues({
                name: '',
                customerId: customerId,
                isActive: false,
                access: API_KEY_ACCESS,
                edgeId: '',
            });
            getdata();
        }
    };
    const handleAccessUpdateSuccess = (res) => {
        if (res) {
            toast.success('API Key Access Updated');
            setAddKey({
                status: false,
                isEdit: false,
            });
            setInitialValues({
                name: '',
                customerId: customerId,
                isActive: false,
                access: API_KEY_ACCESS,
                edgeId: '',
            });
            getdata();
        }
    };

    return (
        <div className={classes.AllDevices}>
            <DeleteModal
                deletefunction={handleDelete}
                opendeleteModal={deleteModal.status}
                setOpenDeleteModal={(status) => setDeleteModal({ ...deleteModal, status })}
            ></DeleteModal>
            <ModalComponent isOpen={apiKeyModal.status} setOpen={(status) => setApiKeyModal({ ...apiKeyModal, status })}>
                <Typography content={'API Key'} />
                <div style={{ minWidth: '25vw' }}>
                    <CustomTooltip content={'Click to copy'}>
                        <CopyToClipboard text={apiKeyModal?.key?.key} onCopy={() => toast.success('Copied!')}>
                            <div className={classes.copyToClipBoardWrapper}>
                                <span className={classes.copyToClipBoardKey}>{apiKeyModal?.key?.key}</span>

                                <UilCopy size={'1.2vw'} style={{ color: 'var(--color-primary)' }} />
                            </div>
                        </CopyToClipboard>
                    </CustomTooltip>
                </div>
                <div className={classes.ButtonContainer}>
                    <div>
                        <button type="submit" className="btn-primary" onClick={() => setApiKeyModal({ status: false, key: {} })}>
                            Close
                        </button>
                    </div>
                </div>
            </ModalComponent>
            <ModalComponent
                isOpen={addkey.status}
                setOpen={(e) => {
                    setAddKey({ status: e, isEdit: false });
                    setInitialValues({
                        name: '',
                        customerId: customerId,
                        isActive: false,
                        access: API_KEY_ACCESS,
                        edgeId: '',
                    });
                }}
                style={{ overflow: 'initial' }}
            >
                <Typography content={addkey.isEdit ? 'Edit API Access' : 'Add API Key'} />
                <Formik initialValues={initialValues} validationSchema={CreateApiValidation} onSubmit={handleApiCreate} enableReinitialize>
                    {({ errors, touched, values, isValidating, setFieldValue, ...props }) => {
                        const handleAccessToggle = (item) => {
                            if (item === 'edge' && values.access.gateway) {
                                setFieldValue('access.gateway', false); // Turn off gateway if edge is toggled
                            } else if (item === 'gateway' && values.access.edge) {
                                setFieldValue('access.edge', false); // Turn off edge if gateway is toggled
                            }
                            setFieldValue(`access.${item}`, !values.access[item]); // Toggle the selected access
                        };
                        return (
                            <Form>
                                <div className={classes.InputContainer}>
                                    <div className={classes.FieldControl2}>
                                        <label for="name">
                                            Name <span className="required">*</span>
                                        </label>
                                        <Input
                                            name="name"
                                            id="name"
                                            type="text"
                                            disabled={addkey.isEdit ? true : false}
                                            style={{
                                                marginTop: '0',
                                                padding: '0.1vw 0 0.1vw 0.5vw',
                                            }}
                                        />
                                    </div>
                                    <div className={classes.FieldControl2}>
                                        <label for="customerId">
                                            Customer Id <span className="required">*</span>
                                        </label>
                                        <DropdownComponent
                                            name="customerId"
                                            id="customerId"
                                            options={customersApi}
                                            defaultValue={{ value: initialValues.customerId }}
                                            disabled={addkey.isEdit ? true : false || customerId ? true : false}
                                        />
                                    </div>
                                </div>
                                <div className={classes.FieldControl2}>
                                    <label for="name">Access</label>
                                    <div className={`flex-box`} style={{ marginTop: '0' }}>
                                        {Object.keys(API_KEY_ACCESS).map((item) => (
                                            <div>
                                                <ToggleButton
                                                    values={values}
                                                    label={Capitalize(item)}
                                                    onClick={() => handleAccessToggle(item)}
                                                    name={`access[${item}]`}
                                                    style={{ marginTop: '0' }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className={classes.InputContainer}>
                                    <div className={classes.FieldControl2}>
                                        <label for="name">Active </label>
                                        <ToggleButton values={values} label={'Active'} name={`isActive`} style={{ marginTop: '0' }} />
                                    </div>
                                    {values?.access?.edge && (
                                        <div className={classes.FieldControl2}>
                                            <label for="edgeId">
                                                Edge <span className="required">*</span>
                                            </label>
                                            <DropdownComponent name="edgeId" id="edgeId" options={edgeDevices} defaultValue={{ value: initialValues.edgeId }} />
                                        </div>
                                    )}
                                    {values?.access?.gateway && (
                                        <div className={classes.FieldControl2}>
                                            <label for="edgeId">
                                                Gateway <span className="required">*</span>
                                            </label>
                                            <DropdownComponent name="edgeId" id="edgeId" options={edgeGateways} defaultValue={{ value: initialValues.edgeId }} />
                                        </div>
                                    )}
                                </div>

                                <div className={classes.ButtonContainer}>
                                    <div>
                                        <button type="submit" className="btn-primary">
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </ModalComponent>
            <div className={classes.Header}>
                <div>
                    <Typography content="External Api Keys" />
                    <div className={classes.TableCount}>
                        Total Count :
                        <span>
                            <Typography size="14" content={customers?.length || 0} />
                        </span>
                    </div>
                    <div>
                        <button
                            type="submit"
                            className="btn-primary"
                            style={{ marginTop: '0' }}
                            onClick={() =>
                                setAddKey({
                                    status: true,
                                    isEdit: false,
                                })
                            }
                        >
                            Add Key
                        </button>
                    </div>
                </div>
            </div>
            <div>
                <Table
                    head={['S.No', 'Customer Name', 'Name', 'Edge', 'Gateway', 'Created At', 'Action']}
                    keys={['s.no', 'customerName', 'name', 'edge', 'edgeGateway', 'createdAt', 'action']}
                    data={customers}
                />
            </div>
        </div>
    );
}

export default ExternalApis;
